/* BarCardScanner — bottom sheet that walks the lawyer through a 2-step capture
   of their Iraqi Bar Association card during signup:

     Step 1 (FRONT)  → Claude Vision extracts: name, bar_id_number, validity
                       grade, issue date.
     Step 2 (BACK)   → pyzbar decodes the CODE128 barcode (numeric).

   On finish, onComplete({ name, bar_id_number, bar_barcode, ...}) is called
   so the parent form can prefill its fields. Works on iPhone + Android via
   <input type="file" accept="image/*" capture="environment">.
*/
import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Camera, CheckCircle, X, ArrowRight, IdentificationCard } from "@phosphor-icons/react";
import { scanBarCardFront, scanBarCardBack } from "../../lib/api";

const STEP_FRONT = "front";
const STEP_BACK = "back";
const STEP_DONE = "done";

function StepPill({ active, done, label, n }) {
  const base =
    "flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold transition-colors";
  if (done) return <span className={`${base} bg-[#166534]/10 text-[#166534]`}><CheckCircle size={12} weight="fill" /> {label}</span>;
  if (active) return <span className={`${base} bg-[#0A192F] text-white`}>{n} · {label}</span>;
  return <span className={`${base} bg-[#EAEAEA] text-[#8A8A8A]`}>{n} · {label}</span>;
}

function CaptureSlot({ label, hint, preview, onPick, disabled, testid }) {
  const inputRef = useRef(null);
  return (
    <div className="space-y-3">
      <button
        type="button"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        data-testid={testid}
        className="w-full aspect-[16/10] border-2 border-dashed border-[#D4AF37]/60 rounded-2xl bg-[#FAF9F6] flex flex-col items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-60"
      >
        {preview ? (
          <img src={preview} alt={label} className="w-full h-full object-cover rounded-2xl" />
        ) : (
          <>
            <Camera size={36} weight="duotone" color="#B8860B" />
            <div className="font-tajawal text-[15px] font-bold text-[#0A192F]">{label}</div>
            <div className="text-[12px] text-[#5A6470]">{hint}</div>
          </>
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPick(f);
          e.target.value = "";
        }}
      />
    </div>
  );
}

function EditField({ name, label, hint, value, onChange, dir: textDir = "auto", maxLength }) {
  return (
    <label className="block">
      <span className="text-[11px] text-[#5A6470] block mb-1">{label}</span>
      <input
        type="text"
        value={value ?? ""}
        onChange={(e) => onChange(name, e.target.value)}
        placeholder={hint || ""}
        maxLength={maxLength}
        dir={textDir}
        data-testid={`bar-card-edit-${name}`}
        className="w-full bg-white border border-[#D4AF37]/40 rounded-xl px-3 py-2 text-[14px] font-bold text-[#0A192F] focus:outline-none focus:border-[#B8860B]"
      />
    </label>
  );
}

function ResultCard({ data, onChange }) {
  const set = (k, v) => onChange?.({ ...data, [k]: v });
  const conf = (data?.confidence || "").toLowerCase();
  return (
    <div className="space-y-3" data-testid="bar-card-result-card">
      <div className="flex items-center gap-2 text-[#166534] font-bold text-[14px]">
        <CheckCircle size={18} weight="fill" />
        تَم استخراج البيانات — راجِعها وعَدِّل ما يَلزَم
      </div>

      {conf === "low" && (
        <div className="bg-[#FFFBEA] border border-[#F0C419]/60 text-[#92400E] text-[12.5px] rounded-xl p-3 leading-relaxed">
          ⚠️ ثقة القراءة <b>منخفضة</b>. تَأَكَّد من كل حَقل أَدناه قَبل المتابعة.
        </div>
      )}

      <div className="grid grid-cols-1 gap-3">
        <EditField name="name" label="الاسم الكامل" hint="محمد علي عبد الله" dir="rtl" value={data?.name} onChange={set} />
        <div className="grid grid-cols-2 gap-3">
          <EditField name="bar_id_number" label="رقم الهوية" hint="مثل 45821" dir="ltr" maxLength={10} value={data?.bar_id_number} onChange={set} />
          <EditField name="bar_validity_grade" label="الصلاحية" hint="أ / ب / ج / د" dir="rtl" maxLength={2} value={data?.bar_validity_grade} onChange={set} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <EditField name="bar_issue_date" label="تاريخ الإصدار" hint="DD/MM/YYYY" dir="ltr" maxLength={10} value={data?.bar_issue_date} onChange={set} />
          <EditField name="bar_validity_until" label="صالحة لغاية" hint="DD/MM/YYYY" dir="ltr" maxLength={10} value={data?.bar_validity_until} onChange={set} />
        </div>
        {/* Date of birth — the AI reads it from the card if printed; if it
            isn't (common on older cards), the lawyer types it manually.
            This field + bar_id_number together are the unique identity
            key that prevents duplicate signups. */}
        <EditField
          name="date_of_birth"
          label={data?.date_of_birth ? "تاريخ التولّد" : "تاريخ التولّد (لم يُقرأ من البطاقة — أدخِله يدوياً)"}
          hint="DD/MM/YYYY"
          dir="ltr"
          maxLength={10}
          value={data?.date_of_birth}
          onChange={set}
        />
        <EditField name="bar_barcode" label="باركود الظهر" hint="أرقام فقط" dir="ltr" maxLength={32} value={data?.bar_barcode} onChange={set} />
      </div>
    </div>
  );
}

export default function BarCardScanner({ open, onClose, onComplete }) {
  const [step, setStep] = useState(STEP_FRONT);
  const [frontPreview, setFrontPreview] = useState(null);
  const [backPreview, setBackPreview] = useState(null);
  // `result` is the editable record shown on the DONE step.
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [busyHint, setBusyHint] = useState("");

  if (!open) return null;

  const reset = () => {
    // Release any preview blob URLs we created — otherwise repeated rescans
    // in one session leak memory (each capture is up to 12 MB).
    if (frontPreview) URL.revokeObjectURL(frontPreview);
    if (backPreview) URL.revokeObjectURL(backPreview);
    setStep(STEP_FRONT);
    setFrontPreview(null);
    setBackPreview(null);
    setResult(null);
    setBusy(false);
    setBusyHint("");
  };
  const close = () => { reset(); onClose?.(); };

  const handleFront = async (file) => {
    setBusy(true);
    setBusyHint("جارٍ قراءة وَجه البطاقة بالذكاء الاصطناعي… (~5-8 ثوانٍ)");
    setFrontPreview(URL.createObjectURL(file));
    try {
      const r = await scanBarCardFront(file);
      if (!r.ok) {
        toast.error(r.reason || "تعذّر التعرّف على البطاقة. التقط صورة أوضح.");
        setFrontPreview(null);
        return;
      }
      setResult({
        name: r.name || "",
        bar_id_number: r.bar_id_number || "",
        bar_validity_grade: r.validity_grade || "",
        bar_issue_date: r.issue_date || "",
        bar_validity_until: r.validity_until || "",
        date_of_birth: r.date_of_birth || "",
        bar_barcode: "",
        confidence: r.confidence || "medium",
      });
      setStep(STEP_BACK);
      const conf = (r.confidence || "").toLowerCase();
      if (conf === "low") {
        toast.warning("تَم الاستخراج بِثِقة مُنخَفِضة — راجِع البيانات لاحقاً.");
      } else {
        toast.success("تَم استخراج بيانات الوَجه بِنَجاح.");
      }
    } catch (e) {
      toast.error("تعذّر معالجة الصورة. حاول مجدّداً.");
      setFrontPreview(null);
    } finally {
      setBusy(false);
      setBusyHint("");
    }
  };

  const handleBack = async (file) => {
    setBusy(true);
    setBusyHint("جارٍ قراءة الباركود…");
    setBackPreview(URL.createObjectURL(file));
    try {
      const r = await scanBarCardBack(file);
      if (!r.ok) {
        toast.error(r.reason || "تعذّر قراءة الباركود. أعد التقاط صورة الظهر بإضاءة أفضل.");
        // Don't reset back preview — let user try again. NO skip: registration
        // without a verified barcode would defeat the purpose of card scanning.
        return;
      }
      setResult((d) => ({ ...(d || {}), bar_barcode: r.barcode }));
      setStep(STEP_DONE);
      toast.success(`تم قراءة الباركود: ${r.barcode}`);
    } catch (e) {
      toast.error("تعذّر قراءة الباركود.");
      setBackPreview(null);
    } finally {
      setBusy(false);
      setBusyHint("");
    }
  };

  const finish = () => {
    // Frontend-side date-of-birth validation before we ship the payload:
    // the backend rejects empty/malformed DOB anyway, but catching it here
    // spares the user a network round-trip and a lost form state.
    const dob = (result?.date_of_birth || "").trim();
    if (!dob) {
      toast.error("تاريخ التولّد مَطلوب — أَضِفهُ في الحَقل قَبل المتابعة.");
      return;
    }
    if (!/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dob)) {
      toast.error("صيغة تاريخ التولّد يَجب أَن تَكون DD/MM/YYYY.");
      return;
    }
    onComplete?.({
      name: result?.name || "",
      bar_id_number: result?.bar_id_number || "",
      bar_validity_grade: result?.bar_validity_grade || "",
      bar_issue_date: result?.bar_issue_date || "",
      bar_validity_until: result?.bar_validity_until || "",
      date_of_birth: dob,
      bar_barcode: result?.bar_barcode || "",
    });
    close();
  };

  // Portal into <body> so we escape <main>'s `.lex-fade` stacking
  // context (opacity animation with fill-mode:both creates a stacking
  // context that traps ANY z-index inside main, meaning the BottomNav
  // sibling at z-[90] paints on top of our z-[100]). Same pattern the
  // messages TopHeader uses to escape its page's stacking context.
  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/55 backdrop-blur-sm flex items-end sm:items-center justify-center"
      onClick={close}
      data-testid="bar-card-scanner"
    >
      <div
        className="w-full sm:max-w-md bg-white sm:rounded-3xl rounded-t-3xl max-h-[92dvh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        dir="rtl"
      >
        <header className="sticky top-0 bg-white border-b border-[#EAEAEA] px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <IdentificationCard size={22} weight="duotone" color="#B8860B" />
            <h2 className="font-tajawal text-[17px] font-bold text-[#0A192F]">مسح بطاقة النقابة</h2>
          </div>
          <button type="button" onClick={close} className="p-1.5 text-[#8A8A8A] hover:text-[#0A192F]" data-testid="bar-card-close">
            <X size={20} />
          </button>
        </header>

        <div className="flex items-center justify-center gap-2 py-3 border-b border-[#EAEAEA]">
          <StepPill n="1" label="الوجه" active={step === STEP_FRONT} done={step !== STEP_FRONT} />
          <ArrowRight size={14} color="#8A8A8A" />
          <StepPill n="2" label="الظهر" active={step === STEP_BACK} done={step === STEP_DONE} />
        </div>

        <div className="p-5 space-y-4">
          {step === STEP_FRONT && (
            <>
              <p className="text-[13px] text-[#5A6470] leading-relaxed text-start">
                التقط صورة <b>وجه</b> بطاقة هويتك (الجهة التي تحوي الصورة الشخصية والاسم). تأكد من وضوح الإضاءة وعدم وجود انعكاس.
              </p>
              <CaptureSlot
                label="تصوير الوجه"
                hint="اضغط لفتح الكاميرا"
                preview={frontPreview}
                onPick={handleFront}
                disabled={busy}
                testid="bar-card-front-capture"
              />
              {/* Manual-entry escape hatch INTENTIONALLY REMOVED (2026-07-07).
                  Every lawyer MUST pass through the AI scan — a text-only
                  bypass would let anyone type invented data and register as
                  a "verified" lawyer, gutting the platform's core trust
                  guarantee to clients. */}
            </>
          )}

          {step === STEP_BACK && (
            <>
              <p className="text-[13px] text-[#5A6470] leading-relaxed text-start">
                ممتاز! الآن اقلب البطاقة وصوّر <b>ظهرها</b> (الجهة التي تحوي الباركود).
              </p>
              <CaptureSlot
                label="تصوير الظهر"
                hint="اضغط لفتح الكاميرا"
                preview={backPreview}
                onPick={handleBack}
                disabled={busy}
                testid="bar-card-back-capture"
              />
              {result && (
                <div className="text-[12px] text-[#5A6470] bg-[#FAF9F6] rounded-xl p-3 text-start">
                  <div>الاسم المُستخرَج: <b className="text-[#0A192F]">{result.name || "—"}</b></div>
                  <div>رقم الهوية: <b className="text-[#0A192F]">{result.bar_id_number || "—"}</b></div>
                </div>
              )}
              {/* Skip-barcode escape hatch INTENTIONALLY REMOVED (2026-07-07).
                  The barcode is the anti-fraud primitive — without it we
                  can't detect the same card being reused across accounts.
                  Better UX: prompt user to retake the photo. */}
            </>
          )}

          {step === STEP_DONE && result && (
            <>
              <ResultCard data={result} onChange={setResult} />
              <button
                type="button"
                onClick={finish}
                data-testid="bar-card-finish"
                className="w-full bg-[#0A192F] text-white py-3 rounded-full text-[14px] font-bold active:scale-[0.98] transition-transform disabled:opacity-60"
                disabled={busy}
              >
                استخدم هذه البيانات في التسجيل
              </button>
            </>
          )}

          {busy && (
            <div className="text-center text-[13px] text-[#B8860B] py-2 leading-relaxed" data-testid="bar-card-busy">
              ⏳ {busyHint || "جاري المعالجة بالذكاء الاصطناعي..."}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
